import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider, useLang } from "@/context/LanguageContext";
import { BookmarkProvider } from "@/context/BookmarkContext";
import { AuthProvider } from "@/context/AuthContext";
import { SettingsProvider, useSettings } from "@/context/SettingsContext";
import { AudioPlayerProvider } from "@/context/AudioPlayerContext";
import GlobalAudioPlayer from "@/components/GlobalAudioPlayer";
import Analytics from "@/components/Analytics";
import { Toaster } from "@/components/ui/sonner";
import ScrollToTop from "@/components/ScrollToTop";
import TypographyLoader from "@/components/TypographyLoader";
import { scopeForPath } from "@/lib/typographyRoles";
import EngagementNudge from "@/components/EngagementNudge";
import { BootLoader, RouteOmTransition } from "@/components/OmLoader";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import LegalPage from "@/pages/LegalPage";
import ExplorePage from "@/pages/ExplorePage";
import CategoryPage from "@/pages/CategoryPage";
import KathaReader from "@/pages/KathaReader";
import KnowledgeDetail from "@/pages/KnowledgeDetail";
import Glossary from "@/pages/Glossary";
import Sources from "@/pages/Sources";
import About from "@/pages/About";
import SearchPage from "@/pages/SearchPage";
import Bookmarks from "@/pages/Bookmarks";
import NotFound from "@/pages/NotFound";
import Services from "@/pages/Services";
import ServicesOverview from "@/pages/ServicesOverview";
import ServiceDetail from "@/pages/ServiceDetail";
import Panchang from "@/pages/Panchang";
import PanchangHowItWorks from "@/pages/PanchangHowItWorks";
import CityPanchang from "@/pages/CityPanchang";
import MuhurtaIndex from "@/pages/MuhurtaIndex";
import ProSignup from "@/pages/ProSignup";
import ProPortal from "@/pages/ProPortal";
import AdminMobileAuth from "@/pages/admin/AdminMobileAuth";
import AdminIntegrations from "@/pages/admin/AdminIntegrations";
import AdminProfessionals from "@/pages/admin/AdminProfessionals";
import CityFestivalMuhurta from "@/pages/CityFestivalMuhurta";
import WhatsNew from "@/pages/WhatsNew";
import Temples from "@/pages/Temples";
import TempleDetail from "@/pages/TempleDetail";
import Ebooks from "@/pages/Ebooks";
import EbookDetail from "@/pages/EbookDetail";
import FestivalPage from "@/pages/FestivalPage";
import MyChecklists from "@/pages/MyChecklists";
import SharedChecklist from "@/pages/SharedChecklist";
import Quiz from "@/pages/Quiz";
import Deities from "@/pages/Deities";
import AmnayaPeethas from "@/pages/AmnayaPeethas";
import AmnayaPeethaDetail from "@/pages/AmnayaPeethaDetail";
import DeityDetail from "@/pages/DeityDetail";
import Categories from "@/pages/Categories";
import CategoryLanding from "@/pages/CategoryLanding";
import HubSection from "@/pages/HubSection";
import HubDetail from "@/pages/HubDetail";
import GaneshChaturthiHome from "@/pages/GaneshChaturthiHome";
import NavratriHome from "@/pages/NavratriHome";
import PitruPakshaHome from "@/pages/PitruPakshaHome";
import DeviFormDetail from "@/pages/DeviFormDetail";
import MahavidyaHub from "@/pages/MahavidyaHub";
import MahavidyaDetail from "@/pages/MahavidyaDetail";
import KanyaPujan from "@/pages/KanyaPujan";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Account from "@/pages/Account";
import MyBookings from "@/pages/MyBookings";
import AuthCallback from "@/pages/AuthCallback";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminContentForm from "@/pages/admin/AdminContentForm";
import AdminGlossary from "@/pages/admin/AdminGlossary";
import AdminSources from "@/pages/admin/AdminSources";
import AdminServices from "@/pages/admin/AdminServices";
import AdminServiceForm from "@/pages/admin/AdminServiceForm";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminImport from "@/pages/admin/AdminImport";
import AdminSync from "@/pages/admin/AdminSync";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminSeo from "@/pages/admin/AdminSeo";
import AdminLegal from "@/pages/admin/AdminLegal";
import AdminPages from "@/pages/admin/AdminPages";
import AdminHomepage from "@/pages/admin/AdminHomepage";
import AdminPanchangEdit from "@/pages/admin/AdminPanchang";
import AdminPanchangLab from "@/pages/admin/AdminPanchangLab";
import AdminPanchangCities from "@/pages/admin/AdminPanchangCities";
import AdminFestivalMuhurta from "@/pages/admin/AdminFestivalMuhurta";
import AdminTemplesAdmin from "@/pages/admin/AdminTemples";
import AdminDeviImages from "@/pages/admin/AdminDeviImages";
import AdminCollections from "@/pages/admin/AdminCollections";
import AdminEbooks from "@/pages/admin/AdminEbooks";
import AdminFestivalPages from "@/pages/admin/AdminFestivalPages";
import AdminHomePromos from "@/pages/admin/AdminHomePromos";import AdminTypography from "@/pages/admin/AdminTypography";
import AdminSystemNotifications from "@/pages/admin/AdminSystemNotifications";
import AdminVisibility from "@/pages/admin/AdminVisibility";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminEmail from "@/pages/admin/AdminEmail";
import AdminMappings from "@/pages/admin/AdminMappings";
import AdminPremium from "@/pages/admin/AdminPremium";
import PanchangaPremium from "@/pages/PanchangaPremium";
import PanchangamPremiumApp from "@/pages/PanchangamPremiumApp";
import AdminMessages from "@/pages/admin/AdminMessages";
import AdminFeedback from "@/pages/admin/AdminFeedback";
import AdminAudios from "@/pages/admin/AdminAudios";
import AdminAmnaya from "@/pages/admin/AdminAmnaya";
import AdminFestivals from "@/pages/admin/AdminFestivals";
import AdminDonations from "@/pages/admin/AdminDonations";
import Feedback from "@/pages/Feedback";
import Collaborate from "@/pages/Collaborate";
import Ganapati32 from "@/pages/Ganapati32";
import AshtaVinayaka from "@/pages/AshtaVinayaka";
import CuratedAudios from "@/pages/CuratedAudios";
import Support from "@/pages/Support";
import ComingSoon from "@/pages/ComingSoon";
import ComingSoonIndex from "@/pages/ComingSoonIndex";
import Premium from "@/pages/Premium";
import Calendar from "@/pages/Calendar";
import Japa from "@/pages/Japa";
import Jyotirlingas from "@/pages/Jyotirlingas";
import ShaktiPeethas from "@/pages/ShaktiPeethas";
void Jyotirlingas; void ShaktiPeethas;
import DainikPooja from "@/pages/DainikPooja";
import SandhyaVandana from "@/pages/SandhyaVandana";
import Sadhana from "@/pages/Sadhana";
import Discussions from "@/pages/Discussions";
import QnA from "@/pages/QnA";
import ForumThread from "@/pages/ForumThread";
import Groups from "@/pages/Groups";
import GroupDetail from "@/pages/GroupDetail";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import Onboarding from "@/pages/Onboarding";
import Leaderboard from "@/pages/Leaderboard";
import Refer from "@/pages/Refer";
import { UPCOMING } from "@/lib/upcoming";
import Journal from "@/pages/Journal";
import JournalPost from "@/pages/JournalPost";
import SitemapPage from "@/pages/SitemapPage";
import AdminJournal from "@/pages/admin/AdminJournal";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminData from "@/pages/admin/AdminData";
import AdminNavigation from "@/pages/admin/AdminNavigation";
import AdminCommunity from "@/pages/admin/AdminCommunity";
import AdminEvents from "@/pages/admin/AdminEvents";
import AdminAnnouncements from "@/pages/admin/AdminAnnouncements";
import AdminActivity from "@/pages/admin/AdminActivity";
import AdminTraffic from "@/pages/admin/AdminTraffic";
import AdminWhatsApp from "@/pages/admin/AdminWhatsApp";
import AdminZohoCampaigns from "@/pages/admin/AdminZohoCampaigns";
import AdminChangelog from "@/pages/admin/AdminChangelog";
import AdminPageHeadings from "@/pages/admin/AdminPageHeadings";
import AdminSpotlight from "@/pages/admin/AdminSpotlight";
import AdminWaitlist from "@/pages/admin/AdminWaitlist";
import AdminPRD from "@/pages/admin/AdminPRD";
import AdminCleanup from "@/pages/admin/AdminCleanup";
import AdminTrash from "@/pages/admin/AdminTrash";
import AdminContentHub from "@/pages/admin/AdminContentHub";
import AdminSitePagesHub from "@/pages/admin/AdminSitePagesHub";
import AdminLanguagesHub from "@/pages/admin/AdminLanguagesHub";
import MyNotes from "@/pages/MyNotes";
import Changelog from "@/pages/Changelog";
import SupportNudge from "@/components/SupportNudge";
import WelcomeTour from "@/components/WelcomeTour";

// Renamable sections: canonical `path` (first segment = `key`) → list page (+ optional detail page).
// Admins can assign a custom web address in Admin → Sections; old paths auto-redirect to the new one.
const SECTIONS = [
  { key: "kathas", path: "/kathas", list: <CategoryPage type="katha" />, detail: <KathaReader /> },
  { key: "mantras", path: "/mantras", list: <CategoryPage type="mantra" />, detail: <KnowledgeDetail /> },
  { key: "suktas", path: "/suktas", list: <CategoryPage type="sukta" />, detail: <KnowledgeDetail /> },
  { key: "stotras", path: "/stotras", list: <CategoryPage type="stotra" />, detail: <KnowledgeDetail /> },
  { key: "puja-vidhi", path: "/rituals", list: <CategoryPage type="puja_vidhi" />, detail: <KnowledgeDetail /> },
  { key: "festivals", path: "/festivals", list: <CategoryPage type="festival" />, detail: <KnowledgeDetail /> },
  { key: "granthas", path: "/granthas", list: <CategoryPage type="grantha" />, detail: <KathaReader /> },
  { key: "scriptures", path: "/scriptures", list: <HubSection section="scriptures" /> },
  { key: "rishis", path: "/rishis", list: <HubSection section="rishis" /> },
  { key: "philosophy", path: "/philosophy", list: <HubSection section="concepts" /> },
  { key: "pilgrimage", path: "/pilgrimage", list: <HubSection section="pilgrimage" /> },
  { key: "samskaras", path: "/samskaras", list: <HubSection section="samskara" /> },
  { key: "deities", path: "/deities", list: <Deities />, detail: <DeityDetail /> },
  { key: "categories", path: "/categories", list: <Categories />, detail: <CategoryLanding /> },
  { key: "temples", path: "/temples", list: <Temples />, detail: <TempleDetail /> },
  { key: "panchang", path: "/panchang", list: <Panchang /> },
  { key: "quiz", path: "/quiz", list: <Quiz /> },
  { key: "journal", path: "/patrika", list: <Journal />, detail: <JournalPost /> },
  { key: "glossary", path: "/glossary", list: <Glossary /> },
  { key: "sources", path: "/sources", list: <Sources /> },
];

// Consolidated/merged sections → permanent client redirects so old links keep working.
const SECTION_MERGES = [
  { from: "/vedic-knowledge", to: "/suktas", withParam: true },
  { from: "/explained", to: "/philosophy" },
  { from: "/traditions", to: "/philosophy" },
  { from: "/vrata", to: "/festivals" },
  { from: "/puja-vidhi", to: "/rituals", withParam: true },
  { from: "/journal", to: "/patrika", withParam: true },
];

// Redirect a detail URL (with :slug) from an old base path to the active one.
function ParamRedirect({ base }) {
  const { slug } = useParams();
  return <Navigate to={`${base}/${slug}`} replace />;
}

function RootRoutes() {
  const { routeSlugs, loaded, config } = useSettings();
  // Wait for settings so custom section addresses resolve before first paint.
  if (!loaded) return <div className="min-h-screen bg-[var(--parchment)]" />;

  // Pages Manager URL aliases: an alias path redirects to its canonical page.
  const aliasRoutes = [];
  Object.entries((config && config.pages) || {}).forEach(([canonical, v]) => {
    const alias = (v.slug || "").trim();
    if (alias && alias.startsWith("/") && alias !== canonical) {
      aliasRoutes.push(<Route key={`alias-${alias}`} path={alias} element={<Navigate to={canonical} replace />} />);
    }
  });

  const sectionRoutes = [];
  SECTIONS.forEach((s) => {
    const custom = routeSlugs[s.key];
    const active = custom ? `/${custom}` : s.path;
    sectionRoutes.push(<Route key={active} path={active} element={s.list} />);
    if (s.detail) sectionRoutes.push(<Route key={`${active}/d`} path={`${active}/:slug`} element={s.detail} />);
    if (active !== s.path) {
      sectionRoutes.push(<Route key={`${s.path}-r`} path={s.path} element={<Navigate to={active} replace />} />);
      if (s.detail) sectionRoutes.push(<Route key={`${s.path}/d-r`} path={`${s.path}/:slug`} element={<ParamRedirect base={active} />} />);
    }
  });

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        {aliasRoutes}
        <Route path="/explore" element={<ExplorePage />} />
        {sectionRoutes}
        {SECTION_MERGES.map((m) => (
          <Route key={m.from} path={m.from} element={<Navigate to={m.to} replace />} />
        ))}
        {SECTION_MERGES.filter((m) => m.withParam).map((m) => (
          <Route key={`${m.from}/p`} path={`${m.from}/:slug`} element={<ParamRedirect base={m.to} />} />
        ))}
        <Route path="/learn/:slug" element={<HubDetail />} />
        <Route path="/hub/:sectionKey" element={<HubSectionRoute />} />
        <Route path="/panchang/how-it-works" element={<PanchangHowItWorks />} />
        <Route path="/panchangam/:city" element={<CityPanchang />} />
        <Route path="/muhurta" element={<MuhurtaIndex />} />
        <Route path="/join-as-purohit" element={<ProSignup />} />
        <Route path="/pro" element={<ProPortal />} />
        <Route path="/muhurta/:festival" element={<MuhurtaIndex />} />
        <Route path="/muhurta/:festival/:city" element={<CityFestivalMuhurta />} />
        <Route path="/services" element={<ServicesOverview />} />
        <Route path="/services/:kind" element={<Services />} />
        <Route path="/service/:slug" element={<ServiceDetail />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Navigate to="/feedback" replace />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="/collaborate" element={<Collaborate />} />
        <Route path="/ganapati-32" element={<Ganapati32 />} />
        <Route path="/ashtavinayaka" element={<AshtaVinayaka />} />
        <Route path="/audios" element={<CuratedAudios />} />
        <Route path="/support" element={<Support />} />
        <Route path="/books" element={<Ebooks />} />
        <Route path="/books/:slug" element={<EbookDetail />} />
        <Route path="/festival/:slug" element={<FestivalPage />} />
        <Route path="/checklists" element={<MyChecklists />} />
        <Route path="/checklist/:shareId" element={<SharedChecklist />} />
        <Route path="/premium" element={<Premium />} />
        <Route path="/panchanga-premium" element={<PanchangaPremium />} />
        <Route path="/coming-soon" element={<ComingSoonIndex />} />
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/whats-new" element={<WhatsNew />} />
        <Route path="/whats-new/:section" element={<WhatsNew />} />
        <Route path="/japa" element={<Japa />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/invite" element={<Refer />} />
        <Route path="/ganesh-chaturthi" element={<GaneshChaturthiHome />} />
        <Route path="/pitru-paksha" element={<PitruPakshaHome />} />
        <Route path="/navratri" element={<NavratriHome />} />
        <Route path="/navratri/kanya-pujan" element={<KanyaPujan />} />
        <Route path="/navratri/:slug" element={<DeviFormDetail />} />
        <Route path="/mahavidya" element={<MahavidyaHub />} />
        <Route path="/amnaya-peethas" element={<AmnayaPeethas />} />
        <Route path="/amnaya-peethas/:slug" element={<AmnayaPeethaDetail />} />
        <Route path="/mahavidya/:slug" element={<MahavidyaDetail />} />
        <Route path="/refer" element={<Navigate to="/invite" replace />} />
        <Route path="/jyotirlingas" element={<Navigate to="/temples" replace />} />
        <Route path="/shakti-peethas" element={<Navigate to="/temples" replace />} />
        <Route path="/dainik-pooja" element={<DainikPooja />} />
        <Route path="/sandhya-vandana" element={<SandhyaVandana />} />
        <Route path="/sadhana" element={<Sadhana />} />
        <Route path="/discussions" element={<Discussions />} />
        <Route path="/discussions/:id" element={<ForumThread />} />
        <Route path="/qna" element={<QnA />} />
        <Route path="/qna/:id" element={<ForumThread />} />
        <Route path="/namasmarana-groups" element={<Navigate to="/groups" replace />} />
        <Route path="/groups" element={<Groups />} />
        <Route path="/groups/:gid/:id" element={<ForumThread />} />
        <Route path="/groups/:id" element={<GroupDetail />} />
        <Route path="/events" element={<Events />} />
        <Route path="/events/:id" element={<EventDetail />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/live-temples" element={<Navigate to="/live-darshan" replace />} />
        <Route path="/japa-live" element={<Navigate to="/japa" replace />} />
        {UPCOMING.map((f) => (
          <Route key={f.path} path={f.path} element={<ComingSoon slug={f.slug} />} />
        ))}
        <Route path="/search" element={<SearchPage />} />
        <Route path="/sitemap" element={<SitemapPage />} />
        <Route path="/bookmarks" element={<Bookmarks />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/account" element={<Account />} />
        <Route path="/notes" element={<MyNotes />} />
        <Route path="/changelog" element={<Changelog />} />
        <Route path="/terms" element={<LegalPage pageKey="terms" />} />
        <Route path="/privacy" element={<LegalPage pageKey="privacy" />} />
        <Route path="/disclaimer" element={<LegalPage pageKey="disclaimer" />} />
        <Route path="/dpdp" element={<LegalPage pageKey="dpdp" />} />
        <Route path="/my-bookings" element={<MyBookings />} />
      </Route>
      {/* Premium app — standalone shell (own header + bottom nav, no site chrome/footer) */}
      <Route path="/panchanga-premium/app" element={<PanchangamPremiumApp />} />
      <Route path="/panchangam-premium/app" element={<PanchangamPremiumApp />} />
      <Route path="/auth/google" element={<AuthCallback />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminOverview />} />
        <Route path="prd" element={<AdminPRD />} />
        <Route path="cleanup" element={<AdminCleanup />} />
        <Route path="trash" element={<AdminTrash />} />
        <Route path="content" element={<AdminContentHub />} />
        <Route path="content/new" element={<AdminContentForm />} />
        <Route path="content/:id" element={<AdminContentForm />} />
        <Route path="glossary" element={<AdminGlossary />} />
        <Route path="sources" element={<AdminSources />} />
        <Route path="languages" element={<AdminLanguagesHub />} />
        <Route path="translations" element={<AdminLanguagesHub />} />
        <Route path="services" element={<AdminServices />} />
        <Route path="services/new" element={<AdminServiceForm />} />
        <Route path="services/:id" element={<AdminServiceForm />} />
        <Route path="bookings" element={<AdminBookings />} />
        <Route path="collections" element={<AdminCollections />} />
        <Route path="ebooks" element={<AdminEbooks />} />
        <Route path="festival-pages" element={<AdminFestivalPages />} />
        <Route path="home-festivals" element={<AdminHomePromos />} />
        <Route path="taxonomy" element={<AdminCollections only={["categories", "deities", "temples"]} showImport />} />
        <Route path="typography" element={<AdminTypography />} />
        <Route path="system-notifications" element={<AdminSystemNotifications />} />
        <Route path="community" element={<AdminCommunity />} />
        <Route path="events" element={<AdminEvents />} />
        <Route path="announcements" element={<AdminAnnouncements />} />
        <Route path="activity" element={<AdminActivity />} />
        <Route path="traffic" element={<AdminTraffic />} />
        <Route path="changelog" element={<AdminChangelog />} />
        <Route path="headings" element={<AdminPageHeadings />} />
        <Route path="spotlight" element={<AdminSpotlight />} />
        <Route path="waitlist" element={<AdminWaitlist />} />
        <Route path="visibility" element={<AdminVisibility />} />
        <Route path="navigation" element={<AdminNavigation />} />
        <Route path="journal" element={<AdminJournal />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="data" element={<AdminData />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="email" element={<AdminEmail />} />
        <Route path="whatsapp" element={<AdminWhatsApp />} />
        <Route path="zoho-campaigns" element={<AdminZohoCampaigns />} />
        <Route path="mappings" element={<AdminMappings />} />
        <Route path="premium" element={<AdminPremium />} />
        <Route path="messages" element={<AdminMessages />} />
        <Route path="feedback" element={<AdminFeedback />} />
        <Route path="audios" element={<AdminAudios />} />
        <Route path="amnaya" element={<AdminAmnaya />} />
        <Route path="festivals" element={<AdminFestivals />} />
        <Route path="donations" element={<AdminDonations />} />
        <Route path="import" element={<AdminImport />} />
        <Route path="media" element={<AdminMedia />} />
        <Route path="homepage" element={<AdminHomepage />} />
        <Route path="temples" element={<AdminTemplesAdmin />} />
        <Route path="devi-images" element={<AdminDeviImages />} />
        <Route path="panchang" element={<AdminPanchangEdit />} />
        <Route path="panchang-lab" element={<AdminPanchangLab />} />
        <Route path="panchang-cities" element={<AdminPanchangCities />} />
        <Route path="festival-muhurta" element={<AdminFestivalMuhurta />} />
        <Route path="professionals" element={<AdminProfessionals />} />
        <Route path="mobile-auth" element={<AdminMobileAuth />} />
        <Route path="integrations" element={<AdminIntegrations />} />
        <Route path="sync" element={<AdminSync />} />
        <Route path="seo" element={<AdminSeo />} />
        <Route path="legal" element={<AdminLegal />} />
        <Route path="pages" element={<AdminPages />} />
        <Route path="site-pages" element={<AdminSitePagesHub />} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
function HubSectionRoute() {
  const { sectionKey } = useParams();
  return <HubSection key={sectionKey} section={sectionKey} />;
}

// Sets body[data-tg-scope] per route so admin-chosen typography stays scoped
// (admin console excluded; per-page overrides only affect their own pages).
function TypographyScope() {
  const { pathname } = useLocation();
  useEffect(() => {
    document.body.dataset.tgScope = pathname.startsWith("/admin") ? "admin" : scopeForPath(pathname);
  }, [pathname]);
  return null;
}

// Keeps the LanguageContext aware of the active pathname so per-page language
// overrides resolve to the right page.
function LangRouteSync() {
  const { pathname } = useLocation();
  const { setLangPath } = useLang();
  useEffect(() => { setLangPath(pathname); }, [pathname, setLangPath]);
  return null;
}

// Remount the routed pages when the active language changes so localized data
// refetches cleanly (no full-page reload / blank flash). en<->hi share one key
// (client-localized, instant); custom languages get their own key.
function KeyedRoutes() {
  const { lang } = useLang();
  const key = lang === "en" || lang === "hi" ? "base" : lang;
  return <RootRoutes key={key} />;
}



function App() {
  return (
    <div className="App grain">
      <HelmetProvider>
        <LanguageProvider>
          <AuthProvider>
            <BookmarkProvider>
              <SettingsProvider>
                <AudioPlayerProvider>
                <BootLoader />
                <Analytics />
                <TypographyLoader />
                <BrowserRouter>
                  <ScrollToTop />
                  <LangRouteSync />
                  <RouteOmTransition />
                  <TypographyScope />
                  <KeyedRoutes />
                  <EngagementNudge />
                  <SupportNudge />
                  <WelcomeTour />
                  <GlobalAudioPlayer />
                </BrowserRouter>
                <Toaster position="bottom-right" />
                </AudioPlayerProvider>
              </SettingsProvider>
            </BookmarkProvider>
          </AuthProvider>
        </LanguageProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
